import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { X, Wallet as WalletIcon, CreditCard, Sparkles, ShieldCheck, ShoppingBag, AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * UserDossierModal — admin-only deep-dive panel for a single user.
 *
 * Composed of 5 blocks: profile header, wallet, Stripe Connect,
 * AI usage (current month), compliance limits (private sellers only) and
 * activity counts. All data comes from one HTTP call (/admin/user/{id}/dossier)
 * so opening this is cheap.
 */
export default function UserDossierModal({ userId, language, onClose }) {
  const isLV = language === 'lv';
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!userId) return;
    let cancelled = false;
    setLoading(true);
    axios.get(`${API}/admin/user/${userId}/dossier`)
      .then(res => { if (!cancelled) setData(res.data); })
      .catch(err => { if (!cancelled) setError(err?.response?.data?.detail || err.message); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [userId]);

  if (!userId) return null;

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-start justify-center p-4 overflow-y-auto" onClick={onClose}>
      <div
        className="bg-white rounded-2xl max-w-3xl w-full my-8 shadow-xl"
        onClick={e => e.stopPropagation()}
        data-testid="user-dossier-modal"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-[#E5E4E0]">
          <h2 className="font-outfit text-lg font-bold text-[#121212]">
            {isLV ? 'Lietotāja profils' : 'User dossier'}
          </h2>
          <Button type="button" size="sm" variant="ghost" onClick={onClose} className="h-8 w-8 p-0" data-testid="dossier-close">
            <X className="w-4 h-4" />
          </Button>
        </div>

        <div className="px-6 py-5 space-y-5">
          {loading && (
            <div className="space-y-3">
              {[...Array(4)].map((_, i) => <div key={i} className="h-20 bg-[#FAFAFA] rounded-xl animate-pulse" />)}
            </div>
          )}

          {error && (
            <div className="rounded-xl bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700">
              {error}
            </div>
          )}

          {data && (
            <>
              {/* Profile header */}
              <div className="flex items-center gap-3 pb-4 border-b border-[#E5E4E0]">
                <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#7C3AED] to-[#D97736] text-white text-lg font-bold flex items-center justify-center">
                  {(data.user.name || '?').charAt(0).toUpperCase()}
                </div>
                <div className="flex-1">
                  <div className="font-medium text-[#121212] text-base">{data.user.name}</div>
                  <div className="text-sm text-[#5C5C5C]">{data.user.email}</div>
                </div>
                <div className="flex flex-wrap gap-1.5">
                  <Badge variant="outline" className="text-xs">{data.user.role}</Badge>
                  {data.user.is_seller && (
                    <Badge variant="outline" className="text-xs bg-[#FFF6F0] border-[#D97736]/30 text-[#D97736]">
                      {data.user.seller_type === 'private' ? (isLV ? 'Privāts' : 'Private') : (isLV ? 'Uzņēmums' : 'Business')}
                    </Badge>
                  )}
                  {data.user.is_verified && (
                    <Badge variant="outline" className="text-xs bg-green-50 border-green-200 text-green-700">
                      <ShieldCheck className="w-3 h-3 mr-1" />{isLV ? 'Verif.' : 'Verif.'}
                    </Badge>
                  )}
                </div>
              </div>

              {/* Wallet */}
              <Section title={isLV ? 'Maciņš' : 'Wallet'} icon={WalletIcon} testid="dossier-wallet">
                <div className="grid grid-cols-3 gap-3">
                  <Stat label={isLV ? 'Atlikums' : 'Balance'} value={`€${data.wallet.balance.toFixed(2)}`} />
                  <Stat label={isLV ? 'Nopelnīts' : 'Earned'} value={`€${data.wallet.total_earned.toFixed(2)}`} />
                  <Stat label={isLV ? 'Iztērēts' : 'Spent'} value={`€${data.wallet.total_spent.toFixed(2)}`} />
                </div>
              </Section>

              {/* Stripe Connect */}
              <Section title="Stripe Connect" icon={CreditCard} testid="dossier-stripe">
                {data.stripe_connect.account_id ? (
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <code className="text-xs bg-[#FAFAFA] border border-[#E5E4E0] px-2 py-1 rounded text-[#121212]">
                        {data.stripe_connect.account_id}
                      </code>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      <Badge variant="outline" className={data.stripe_connect.payouts_enabled ? 'bg-green-50 border-green-200 text-green-700' : 'bg-amber-50 border-amber-200 text-amber-700'}>
                        {isLV ? 'Izmaksas' : 'Payouts'}: {data.stripe_connect.payouts_enabled ? '✓' : '✗'}
                      </Badge>
                      <Badge variant="outline" className={data.stripe_connect.charges_enabled ? 'bg-green-50 border-green-200 text-green-700' : 'bg-amber-50 border-amber-200 text-amber-700'}>
                        {isLV ? 'Maksājumi' : 'Charges'}: {data.stripe_connect.charges_enabled ? '✓' : '✗'}
                      </Badge>
                    </div>
                    {data.withdrawals && Object.keys(data.withdrawals).length > 0 && (
                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 pt-2">
                        {Object.entries(data.withdrawals).map(([status, w]) => (
                          <div key={status} className="rounded-lg bg-[#FAFAFA] border border-[#E5E4E0] p-2.5">
                            <div className="text-[10px] text-[#5C5C5C] uppercase">{status}</div>
                            <div className="text-sm font-medium text-[#121212]">{w.count} · €{w.amount.toFixed(2)}</div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ) : (
                  <p className="text-sm text-[#5C5C5C]">
                    {isLV ? 'Nav pieslēgts Stripe Connect kontam' : 'Not connected to Stripe Connect'}
                  </p>
                )}
              </Section>

              {/* AI Usage */}
              <Section title={isLV ? 'AI patēriņš (šī mēneša)' : 'AI usage (this month)'} icon={Sparkles} testid="dossier-ai">
                <div className="grid grid-cols-3 gap-3 mb-3">
                  <Stat label={isLV ? 'Meklēšana' : 'Search'} value={data.ai_usage.by_scope.parse} />
                  <Stat label={isLV ? 'Sinonīmi' : 'Synonyms'} value={data.ai_usage.by_scope.expand} />
                  <Stat label={isLV ? 'Apraksts' : 'Listing'} value={data.ai_usage.by_scope.suggest} />
                </div>
                <div className="text-xs text-[#5C5C5C]">
                  {isLV ? 'Kopā' : 'Total'}: <span className="font-medium text-[#121212]">{data.ai_usage.total_calls}</span>
                  {' · '}
                  ~{data.ai_usage.total_tokens_est.toLocaleString('lv-LV')} {isLV ? 'tokeni' : 'tokens'}
                </div>
              </Section>

              {/* Compliance limits */}
              {data.compliance && (
                <Section title={isLV ? 'Privāto pārdevēju limiti' : 'Private seller limits'} icon={AlertTriangle} testid="dossier-compliance">
                  <div className="grid grid-cols-3 gap-3">
                    <Stat label={isLV ? 'Gada apgrozījums' : 'Annual sales'} value={`€${data.compliance.annual_sales.toFixed(2)}`} />
                    <Stat label={isLV ? 'Preces' : 'Items sold'} value={data.compliance.annual_items_sold} />
                    <Stat label={isLV ? 'Mēn. izmaksas' : 'Monthly payouts'} value={`€${data.compliance.monthly_payouts.toFixed(2)}`} />
                  </div>
                  {data.compliance.is_paused && (
                    <div className="mt-2 rounded-lg bg-amber-50 border border-amber-200 px-3 py-2 text-xs text-amber-800">
                      {isLV ? 'Konts ir apturēts limita pārsniegšanas dēļ' : 'Account paused for exceeding limits'}
                    </div>
                  )}
                </Section>
              )}

              {/* Activity */}
              <Section title={isLV ? 'Aktivitāte' : 'Activity'} icon={ShoppingBag} testid="dossier-activity">
                <div className="grid grid-cols-3 gap-3">
                  <Stat label={isLV ? 'Sludinājumi' : 'Listings'} value={data.listings_count} />
                  <Stat label={isLV ? 'Pirkumi' : 'As buyer'} value={data.orders_as_buyer} />
                  <Stat label={isLV ? 'Pārdevumi' : 'As seller'} value={data.orders_as_seller} />
                </div>
              </Section>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function Section({ title, icon: Icon, children, testid }) {
  return (
    <div className="rounded-xl bg-[#FAFAFA] border border-[#E5E4E0] p-4" data-testid={testid}>
      <div className="flex items-center gap-2 mb-3">
        <Icon className="w-4 h-4 text-[#5C5C5C]" />
        <h3 className="text-sm font-medium text-[#121212]">{title}</h3>
      </div>
      {children}
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div>
      <div className="text-[10px] text-[#5C5C5C] uppercase">{label}</div>
      <div className="font-outfit text-lg font-bold text-[#121212]">{value}</div>
    </div>
  );
}
